import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import useUserProfile from "../hooks/useUserProfile";
import { removeToken, getToken } from "../utils/token";
import { clearTenantId } from "../utils/tenant";
import useOverviewSearch from "../hooks/useOverviewSearch";
import { isAvatarImgSrc, normalizeAvatarSrc } from "../utils/profileImage";

export default function SidebarLayout({ children, pageTitle: pageTitleProp, breadcrumb, pageSubtitle, titleIcon, titleAddon, headerActions }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(() => {
    try {
      const v = localStorage.getItem("sidebar:isOpen");
      if (v === "0") return false;
      if (v === "1") return true;
    } catch {}
    return true;
  }); // desktop collapse/expand (persisted)
  const [mobileOpen, setMobileOpen] = useState(false); // mobile off-canvas
  const navigate = useNavigate();
  const { data } = useUserProfile();
  const currentUser = data?.authorizedInfo?.profile || null;
  // Resolve role from storage or JWT payload for robustness
  let role = "";
  try { role = (localStorage.getItem("role") || "").toLowerCase(); } catch {}
  if (!role) {
    try {
      const token = getToken();
      if (token && token.includes(".")) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const tokenRole = (payload?.role || (Array.isArray(payload?.roles) ? payload.roles[0] : "") || "").toString().toLowerCase();
        if (tokenRole) {
          role = tokenRole;
          try { localStorage.setItem("role", tokenRole); } catch {}
        }
      }
    } catch {}
  }
  const canUploadBtn = role === "admin" || role === "tenant_admin"  || role === "super_admin";

  const getPageTitle = (pathname) => {
    if (pathname.includes("/settings/add-connector")) return "Add Connector";
    if (pathname.includes("/settings/sync-history")) return "Sync History";
    const segments = pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1] || "overview";
    return last.charAt(0).toUpperCase() + last.slice(1);
  };

  const pageTitle = pageTitleProp || getPageTitle(location.pathname);
  const breadcrumbLabel = breadcrumb || `Dashboard / ${pageTitle}`;

  const handleLogout = () => {
    try {
      removeToken();
      localStorage.removeItem("verificationToken");
      localStorage.removeItem("pendingVerificationEmail");
      try { localStorage.removeItem("mfaEnabled"); } catch {}
      try { localStorage.removeItem("role"); } catch {}
			try { clearTenantId(); } catch {}
    } catch (_) {}
    navigate("/login");
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const { results, loading: loadingSearch, error: searchError, runSearch, clear } = useOverviewSearch();

  const handleSearch = async () => {
    const q = (searchTerm || "").trim();
    if (!q) return;
    await runSearch({ q, limit: 10 });
    setShowSearch(true);
  };

  // compute desktop content offset based on sidebar collapsed state
  const desktopOffsetClass = isOpen ? "md:ml-64" : "md:ml-16";

  // Persist desktop sidebar state across route changes
  React.useEffect(() => {
    try {
      localStorage.setItem("sidebar:isOpen", isOpen ? "1" : "0");
    } catch {}
  }, [isOpen]);

  return (
    <div className="flex min-h-screen bg-gray-100 overflow-x-hidden">
      {/* Sidebar - Desktop (md+) */}
      <aside
        className={`hidden md:flex bg-[#0F172A] text-white flex-col transition-all duration-300
          ${isOpen ? "w-64" : "w-16"} md:fixed md:inset-y-0 md:left-0 md:top-0 md:bottom-0 z-30`}
      >
        {/* Logo + Toggle */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-800">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => { if (!isOpen) setIsOpen(true); }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (!isOpen && (e.key === "Enter" || e.key === " ")) setIsOpen(true); }}
          >
            {isOpen ? (
              <img
                src="/images/sun.png"
                alt="Sun Logo"
                className="h-10 w-13 object-contain"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/icons/CoreLinkSymbol.svg";
                  e.currentTarget.className = "h-10 w-13 filter invert brightness-0";
                }}
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            ) : (
              <img
                src="/icons/CoreLinkSymbol.svg"
                alt="Corelink Logo"
                className="h-10 w-13 filter invert brightness-0"
              />
            )}
          </div>
          {isOpen && (
            <button
              onClick={() => setIsOpen((prev) => !prev)}
              className="text-white hover:text-emerald-400 flex-shrink-0"
            >
              <img
                src="/icons/SideMenuTabs.svg"
                alt="Menu Icon"
                className="h-10 w-12"
              />
            </button>
          )}
        </div>

        {isOpen && (
          <>
            {/* Search */}
            <div className="px-4 pt-4 relative">
              <div className="relative flex items-center bg-white rounded-lg border border-gray-300 shadow-sm">
                <span className="pointer-events-none absolute left-3 text-gray-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
                    />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search"
                  className="flex-1 bg-transparent text-gray-700 placeholder-gray-500 text-sm rounded-lg pl-10 pr-14 py-2 focus:outline-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                />
                <span className="absolute right-2 flex items-center gap-1 px-2 py-1 text-xs text-gray-500 border border-gray-300 rounded-md bg-gray-50">
                  ⌘ K
                </span>
              </div>

              {/* Results Dropdown */}
              {showSearch && (
                <div className="absolute left-4 right-4 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-80 overflow-auto z-20">
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <div className="text-xs text-gray-500">
                      {loadingSearch ? "Searching..." : (searchError ? "Error loading results" : `${results.length} results`)}
                    </div>
                    <button
                      className="text-xs text-gray-500 hover:text-gray-700"
                      onClick={() => { setShowSearch(false); clear(); }}
                    >
                      Close
                    </button>
                  </div>
                  <div className="py-1">
                    {!loadingSearch && !searchError && results.length === 0 && (
                      <div className="px-3 py-2 text-xs text-gray-500">No results found</div>
                    )}
                    {results.map((r, idx) => (
                      <button
                        key={idx}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50"
                        onClick={() => {
                          if (r.link && typeof r.link === "string") {
                            if (r.link.startsWith("/")) {
                              navigate(r.link);
                            } else {
                              window.open(r.link, "_blank");
                            }
                          }
                          setShowSearch(false);
                        }}
                        title={r.title}
                      >
                        <div className="text-[13px] font-medium text-slate-800 truncate">{r.title || "Untitled"}</div>
                        {r.snippet ? (
                          <div className="text-[11px] text-gray-500 truncate">{r.snippet}</div>
                        ) : null}
                        {(r.type || r.date || typeof r.score === "number") ? (
                          <div className="mt-1 inline-flex items-center gap-2">
                            {r.type ? (
                              <span className="rounded-md bg-gray-100 text-gray-600 text-[10px] px-1.5 py-0.5">{r.type}</span>
                            ) : null}
                            {typeof r.score === "number" ? (
                              <span className="rounded-md bg-gray-100 text-gray-600 text-[10px] px-1.5 py-0.5">
                                score: {r.score.toFixed(4)}
                              </span>
                            ) : null}
                            {r.date ? <span className="text-[10px] text-gray-400">{r.date}</span> : null}
                          </div>
                        ) : null}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <hr className="my-4 border-gray-700" />
            </div>

			{/* Project + user email (from profile) */}
			<div className="px-4">
			  <div className="flex items-center gap-4 py-3">
				<div className="h-8 w-8 rounded-lg bg-orange-500 text-white font-semibold flex items-center justify-center">
				  {currentUser?.name ? (currentUser.name.split(" ").map(p => p[0]).join("").slice(0,2)) : "PR"}
				</div>
				<div className="min-w-0">
				  <div className="text-sm font-semibold text-white">My Project</div>
				  <div className="text-xs text-gray-400 truncate">{currentUser?.email || ""}</div>
				</div>
			  </div>
			  <hr className="border-gray-700" />
			</div>

            {/* Navigation */}
            <nav className="flex-1 space-y-1 px-4 mt-4">
              {[
                { to: "/overview", icon: "layout-dashboard.svg", label: "Overview" },
                { to: "/insights", icon: "chart-bar.svg", label: "Insights" },
                { to: "/accuracy", icon: "shield-check.svg", label: "Accuracy" },
                { to: "/infrastructure", icon: "cpu.svg", label: "Infrastructure" },
              ].map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                 className={({ isActive }) =>
  `flex items-center gap-3 py-2.5 px-3 rounded-md transition-colors ${
    isActive
      ? "bg-gray-700/50 text-white"
      : "text-gray-300 hover:bg-gray-700/40"
  }`
}

                >
                  <img
                    src={`/icons/${item.icon}`}
                    alt={item.label}
                    className="h-5 w-5"
                  />
                  {isOpen && <span>{item.label}</span>}
                </NavLink>
              ))}
              {canUploadBtn && (
                <>
                  <NavLink
                    to="/ingestion?next=/chat"
                    className={({ isActive }) =>
                      `flex items-center gap-3 py-2.5 px-3 rounded-md transition-colors ${
                        isActive ? "bg-gray-700/50 text-white" : "text-gray-300 hover:bg-gray-700/40"
                      }`
                    }
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                      <path d="M12 3a.75.75 0 01.75.75v9.19l2.47-2.47a.75.75 0 111.06 1.06l-3.75 3.75a.75.75 0 01-1.06 0l-3.75-3.75a.75.75 0 111.06-1.06l2.47 2.47V3.75A.75.75 0 0112 3z" />
                      <path d="M3.75 15a.75.75 0 01.75.75v2.25A2.25 2.25 0 006.75 20.25h10.5A2.25 2.25 0 0019.5 18v-2.25a.75.75 0 011.5 0V18a3.75 3.75 0 01-3.75 3.75H6.75A3.75 3.75 0 013 18v-2.25A.75.75 0 013.75 15z" />
                    </svg>
                    {isOpen && <span>Upload Document</span>}
                  </NavLink>
                  {/* <NavLink
                    to="/settings/oauth"
                    className={({ isActive }) =>
                      `flex items-center gap-3 py-2.5 px-3 rounded-md transition-colors ${
                        isActive ? "bg-gray-700/50 text-white" : "text-gray-300 hover:bg-gray-700/40"
                      }`
                    }
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                      <path
                        fillRule="evenodd"
                        d="M15.75 1.5a6.751 6.751 0 015.096 11.036 3.75 3.75 0 01-.563 6.068c-.344.067-.793.096-1.337.096H6.954c-.544 0-.993-.029-1.337-.096a3.752 3.752 0 01-.596-7.066 6.75 6.75 0 019.846-11.086 24.867 24.867 0 012.25-.758zM6.698 17.058a2.251 2.251 0 002.964 3.086 8.986 8.986 0 003.086-11.086 6.75 6.75 0 00-9.846 11.086 24.935 24.935 0 002.25.758zm7.058-13.058a15.097 15.097 0 00-9.086 13.086 6.75 6.75 0 0111.086-13.086z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {isOpen && <span>Tenant OAuth</span>}
                  </NavLink> */}
                </>
              )}
            </nav>

            {/* Footer */}
            <div className="mt-auto px-4">
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  `flex items-center gap-3 py-3 px-3 rounded-md ${
                    isActive ? "bg-gray-700/50 text-white" : "text-gray-300 hover:bg-gray-700/40"
                  }`
                }
              >
                <img
                  src="/icons/settings.svg"
                  alt="Settings"
                  className="h-5 w-5"
                />
                {isOpen && <span>Settings</span>}
              </NavLink>
              <hr className="mt-2 border-gray-700" />

              {currentUser && (
                <button
                  type="button"
                  onClick={() => navigate("/profile")}
                  className="w-full py-3 flex items-center gap-3 px-3 rounded-md hover:bg-gray-700/40 text-left"
                  aria-label="Open profile"
                >
                  <div className="h-9 w-9 -ml-3 rounded-full bg-emerald-500/80 flex items-center justify-center text-xs font-semibold overflow-hidden flex-shrink-0">
                    {isAvatarImgSrc(currentUser.image) ? (
                      <img
                        src={normalizeAvatarSrc(currentUser.image)}
                        alt={currentUser.name}
                        className="h-full w-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      currentUser.name ? currentUser.name[0] : "?"
                    )}
                  </div>
                  {isOpen && (
                    <div className="flex-1 min-w-0">
                      <div className="leading-tight space-y-0.5">
                        <div className="text-sm">{currentUser.name}</div>
                        <div className="text-xs text-gray-400">{currentUser.email}</div>
                      </div>
                    </div>
                  )}
                </button>
              )}
            </div>
          </>
        )}

        {!isOpen && (
          <>
            <nav className="flex-1 space-y-1 px-2 py-4">
              {[
                { to: "/overview", icon: "layout-dashboard.svg", label: "Overview" },
                { to: "/insights", icon: "chart-bar.svg", label: "Insights" },
                { to: "/accuracy", icon: "shield-check.svg", label: "Accuracy" },
                { to: "/infrastructure", icon: "cpu.svg", label: "Infrastructure" },
              ].map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center justify-center py-2 rounded-md transition-colors ${
                      isActive ? "bg-gray-700/50 text-white" : "text-gray-300 hover:bg-gray-700/40"
                    }`
                  }
                >
                  <img src={`/icons/${item.icon}`} alt={item.label} className="h-5 w-5" />
                </NavLink>
              ))}
              {canUploadBtn && (
                <>
                  <NavLink
                    to="/ingestion?next=/chat"
                    className={({ isActive }) =>
                      `flex items-center justify-center py-2 rounded-md transition-colors ${
                        isActive ? "bg-gray-700/50 text-white" : "text-gray-300 hover:bg-gray-700/40"
                      }`
                    }
                    title="Upload Document"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                      <path d="M12 3a.75.75 0 01.75.75v9.19l2.47-2.47a.75.75 0 111.06 1.06l-3.75 3.75a.75.75 0 01-1.06 0l-3.75-3.75a.75.75 0 111.06-1.06l2.47 2.47V3.75A.75.75 0 0112 3z" />
                      <path d="M3.75 15a.75.75 0 01.75.75v2.25A2.25 2.25 0 006.75 20.25h10.5A2.25 2.25 0 0019.5 18v-2.25a.75.75 0 011.5 0V18a3.75 3.75 0 01-3.75 3.75H6.75A3.75 3.75 0 013 18v-2.25A.75.75 0 013.75 15z" />
                    </svg>
                  </NavLink>
                  <NavLink
                    to="/settings/oauth"
                    className={({ isActive }) =>
                      `flex items-center justify-center py-2 rounded-md transition-colors ${
                        isActive ? "bg-gray-700/50 text-white" : "text-gray-300 hover:bg-gray-700/40"
                      }`
                    }
                    title="Tenant OAuth"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                      <path
                        fillRule="evenodd"
                        d="M15.75 1.5a6.751 6.751 0 015.096 11.036 3.75 3.75 0 01-.563 6.068c-.344.067-.793.096-1.337.096H6.954c-.544 0-.993-.029-1.337-.096a3.752 3.752 0 01-.596-7.066 6.75 6.75 0 019.846-11.086 24.867 24.867 0 012.25-.758zM6.698 17.058a2.251 2.251 0 002.964 3.086 8.986 8.986 0 003.086-11.086 6.75 6.75 0 00-9.846 11.086 24.935 24.935 0 002.25.758zm7.058-13.058a15.097 15.097 0 00-9.086 13.086 6.75 6.75 0 0111.086-13.086z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </NavLink>
                </>
              )}
            </nav>
            <div className="px-2 pb-3">
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  `flex items-center justify-center py-2 rounded-md transition-colors ${
                    isActive ? "bg-gray-700/50 text-white" : "text-gray-300 hover:bg-gray-700/40"
                  }`
                }
              >
                <img src="/icons/settings.svg" alt="Settings" className="h-5 w-5" />
              </NavLink>
              {currentUser && (
                <button
                  type="button"
                  onClick={() => navigate("/profile")}
                  className="mt-2 w-full flex items-center justify-center py-2 rounded-md transition-colors text-gray-300 hover:bg-gray-700/40"
                  aria-label="Open profile"
                  title="Profile"
                >
                  <div className="h-8 w-8 rounded-full bg-emerald-500/80 flex items-center justify-center text-xs font-semibold overflow-hidden">
                    {isAvatarImgSrc(currentUser.image) ? (
                      <img
                        src={normalizeAvatarSrc(currentUser.image)}
                        alt={currentUser.name}
                        className="h-full w-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      currentUser.name ? currentUser.name[0] : "?"
                    )}
                  </div>
                </button>
              )}
            </div>
          </>
        )}
      </aside>

      {/* Sidebar - Mobile (off-canvas) */}
      <div className={`md:hidden ${mobileOpen ? "fixed inset-0 z-40" : ""}`}>
        {/* Backdrop */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
        )}
        {/* Drawer */}
        <aside
          className={`fixed top-0 left-0 h-full w-64 bg-[#0F172A] text-white z-50 transform transition-transform duration-300 ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-800">
            <div className="flex items-center gap-2">
              <img src="/icons/CoreLinkSymbol.svg" alt="Corelink Logo" className="h-8 w-8 filter invert brightness-0" />
              <span className="text-lg font-semibold leading-tight text-white">CORELINK<br/>AI</span>
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="text-white hover:text-emerald-400 flex-shrink-0"
              aria-label="Close menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          {/* Mobile nav mirrors the expanded desktop navigation */}
          <nav className="flex-1 space-y-1 px-4 py-4 overflow-y-auto">
            {[
              { to: "/overview", icon: "layout-dashboard.svg", label: "Overview" },
              { to: "/insights", icon: "chart-bar.svg", label: "Insights" },
              { to: "/accuracy", icon: "shield-check.svg", label: "Accuracy" },
              { to: "/infrastructure", icon: "cpu.svg", label: "Infrastructure" },
            ].map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 py-2.5 px-3 rounded-md transition-colors ${
                    isActive ? "bg-gray-700/50 text-white" : "text-gray-300 hover:bg-gray-700/40"
                  }`
                }
              >
                <img src={`/icons/${item.icon}`} alt={item.label} className="h-5 w-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
            {canUploadBtn && (
              <>
                <NavLink
                  to="/ingestion?next=/chat"
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 py-2.5 px-3 rounded-md transition-colors ${
                      isActive ? "bg-gray-700/50 text-white" : "text-gray-300 hover:bg-gray-700/40"
                    }`
                  }
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                    <path d="M12 3a.75.75 0 01.75.75v9.19l2.47-2.47a.75.75 0 111.06 1.06l-3.75 3.75a.75.75 0 01-1.06 0l-3.75-3.75a.75.75 0 111.06-1.06l2.47 2.47V3.75A.75.75 0 0112 3z" />
                    <path d="M3.75 15a.75.75 0 01.75.75v2.25A2.25 2.25 0 006.75 20.25h10.5A2.25 2.25 0 0019.5 18v-2.25a.75.75 0 011.5 0V18a3.75 3.75 0 01-3.75 3.75H6.75A3.75 3.75 0 013 18v-2.25A.75.75 0 013.75 15z" />
                  </svg>
                  <span>Upload Document</span>
                </NavLink>
                <NavLink
                  to="/settings/oauth"
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 py-2.5 px-3 rounded-md transition-colors ${
                      isActive ? "bg-gray-700/50 text-white" : "text-gray-300 hover:bg-gray-700/40"
                    }`
                  }
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                    <path
                      fillRule="evenodd"
                      d="M15.75 1.5a6.751 6.751 0 015.096 11.036 3.75 3.75 0 01-.563 6.068c-.344.067-.793.096-1.337.096H6.954c-.544 0-.993-.029-1.337-.096a3.752 3.752 0 01-.596-7.066 6.75 6.75 0 019.846-11.086 24.867 24.867 0 012.25-.758zM6.698 17.058a2.251 2.251 0 002.964 3.086 8.986 8.986 0 003.086-11.086 6.75 6.75 0 00-9.846 11.086 24.935 24.935 0 002.25.758zm7.058-13.058a15.097 15.097 0 00-9.086 13.086 6.75 6.75 0 0111.086-13.086z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Tenant OAuth</span>
                </NavLink>
              </>
            )}
          </nav>
          <div className="px-4 pb-4">
            <NavLink
              to="/settings"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 py-3 px-3 rounded-md ${
                  isActive ? "bg-gray-700/50 text-white" : "text-gray-300 hover:bg-gray-700/40"
                }`
              }
            >
              <img src="/icons/settings.svg" alt="Settings" className="h-5 w-5" />
              <span>Settings</span>
            </NavLink>
          </div>
        </aside>
      </div>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col w-full transition-[margin] duration-300 ${desktopOffsetClass}`}>
        <header className="sticky top-0 z-10 bg-[#0F172A] text-white border-b border-gray-800">
          <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4">
            {/* Left: Logo (mobile) OR breadcrumb/title (desktop) */}
            <div className="flex items-center gap-3">
              {/* Mobile: dashboard logo opens sidebar */}
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-gray-700 text-white"
                aria-label="Open menu"
                onClick={() => setMobileOpen(true)}
              >
                <img src="/icons/CoreLinkSymbol.svg" alt="Corelink" className="h-5 w-5 filter invert brightness-0" />
              </button>
              {/* Desktop: breadcrumb + title */}
              <div className="hidden md:flex md:flex-col">
                <div className="text-xs text-gray-400">{breadcrumbLabel}</div>
                <div className="flex items-center gap-3 mt-1">
                  {titleIcon}
                  <h1 className="text-2xl font-semibold">{pageTitle}</h1>
                  {titleAddon}
                </div>
                {pageSubtitle ? (
                  <div className="text-xs text-gray-400 mt-1">{pageSubtitle}</div>
                ) : null}
              </div>
            </div>
            {/* Right: action buttons */}
            <div className="flex items-center gap-2 md:gap-3">
              {headerActions}
              {/* Mobile: icon-only Chat */}
              <button
                type="button"
                onClick={() => {
                  const token = getToken();
                  if (!token) {
                    navigate("/login");
                    return;
                  }
                  // Always start a new chat from the dashboard Chat button
                  navigate("/chat?new=1");
                }}
                className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-gray-700 text-white"
                aria-label="Chat"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                  <path d="M2.25 12c0-4.556 3.87-8.25 8.625-8.25s8.625 3.694 8.625 8.25-3.87 8.25-8.625 8.25c-.983 0-1.931-.152-2.814-.435a.75.75 0 0 0-.47.023L3.9 21.36a.75.75 0 0 1-1.018-.881l.947-3.545a.75.75 0 0 0-.1-.58A8.164 8.164 0 0 1 2.25 12z" />
                </svg>
              </button>
              {/* Desktop: Chat with label */}
              <button
                type="button"
                onClick={() => {
                  const token = getToken();
                  if (!token) {
                    navigate("/login");
                    return;
                  }
                  // Always start a new chat from the dashboard Chat button
                  navigate("/chat?new=1");
                }}
                className="hidden md:inline-flex items-center gap-2 rounded-[8px] bg-[#37B5A0] hover:bg-[#37B5A0] text-white text-sm font-medium px-4 py-2 transition-colors"
                title="Chat"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                  <path d="M2.25 12c0-4.556 3.87-8.25 8.625-8.25s8.625 3.694 8.625 8.25-3.87 8.25-8.625 8.25c-.983 0-1.931-.152-2.814-.435a.75.75 0 0 0-.47.023L3.9 21.36a.75.75 0 0 1-1.018-.881l.947-3.545a.75.75 0 0 0-.1-.58A8.164 8.164 0 0 1 2.25 12z" />
                </svg>
                Chat
              </button>
              {/* Mobile: icon-only Logout */}
              <button
                type="button"
                onClick={handleLogout}
                className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-gray-700 text-white"
                aria-label="Logout"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                  <path fillRule="evenodd" d="M15.75 3a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0V4.5h-6A2.25 2.25 0 0 0 6.75 6.75v10.5A2.25 2.25 0 0 0 9 19.5h6v-2.25a.75.75 0 0 1 1.5 0v3A.75.75 0 0 1 15.75 21h-7.5A3.75 3.75 0 0 1 4.5 17.25V6.75A3.75 3.75 0 0 1 8.25 3h7.5zm3.53 8.47a.75.75 0 0 1 0 1.06l-3 3a.75.75 0 1 1-1.06-1.06l1.72-1.72H10.5a.75.75 0 0 1 0-1.5h6.44l-1.72-1.72a.75.75 0 1 1 1.06-1.06l3 3z" clipRule="evenodd" />
                </svg>
              </button>
              {/* Desktop: Logout with label */}
              <button
                type="button"
                onClick={handleLogout}
                className="hidden md:inline-flex items-center gap-2 rounded-[8px] bg-[#37B5A0] hover:bg-[#37B5A0] text-white text-sm font-medium px-4 py-2 transition-colors"
                title="Log out"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                  <path fillRule="evenodd" d="M15.75 3a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0V4.5h-6A2.25 2.25 0 0 0 6.75 6.75v10.5A2.25 2.25 0 0 0 9 19.5h6v-2.25a.75.75 0 0 1 1.5 0v3A.75.75 0 0 1 15.75 21h-7.5A3.75 3.75 0 0 1 4.5 17.25V6.75A3.75 3.75 0 0 1 8.25 3h7.5zm3.53 8.47a.75.75 0 0 1 0 1.06l-3 3a.75.75 0 1 1-1.06-1.06l1.72-1.72H10.5a.75.75 0 0 1 0-1.5h6.44l-1.72-1.72a.75.75 0 1 1 1.06-1.06l3 3z" clipRule="evenodd" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 min-w-0">{children}</main>
      </div>
    </div>
  );
}
